// Distribution-domain list filters: merchandisers (PD-13923) and sensor
// devices (PD-13926). Both lists are backed by dedicated entities/endpoints
// (/api/merchandisers, /api/sensor-devices) rather than the generic object
// list, so their filters live here and are referenced by `activeFilters` in
// the corresponding list-builder configs.

import { createKeywordFilter, createStatusFilter } from './factories';
import type { FilterRegistry } from './types';

export const distributionFilters: FilterRegistry = {
	// --- Merchandiser list ---
	merchandiserSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search merchandisers...',
			objectTable: 'merchandiser',
			listKeys: ['merchandiser'],
		}),
		queryParamName: 'search',
	},

	merchandiserStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'merchandiser',
			fieldName: 'status',
			listKeys: ['merchandiser'],
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
				{ id: 'maintenance', label: 'Maintenance' },
				{ id: 'offline', label: 'Offline' },
			],
		}),
		queryParamName: 'merchandiserStatus',
	},

	// Placement (PD-15560) — ONE combined typeahead over both places a unit can
	// sit: a warehouse (merchandiser.fk_warehouse__id) or a client location
	// (fk_client_address__id). The two are mutually exclusive on a row, so a
	// single filter reads better than two, and the option menu groups them under
	// headings from each option's `type` (see BaseMultiSelect). Option ids are
	// prefixed with their source (`warehouse:` / `clientAddress:`) so the
	// merchandisers endpoint can split the selection back apart — no
	// queryMapping, mirroring the other filters on this dedicated endpoint.
	merchandiserPlacement: {
		type: 'multi-select',
		label: 'Placement',
		placeholder: 'Search warehouses or client locations...',
		colorScheme: 'teal',
		searchable: true,
		loadOnFocus: true,
		menuMinWidth: 320,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			secondary: 'subtitle' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/merchandisers/placement/options',
		queryParamName: 'placement',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// Make & Model — the manufacturer/model catalog pairs that exist on units,
	// as one combined typeahead ("Leer — Model 60 Indoor Glass"). Option ids
	// are `<manufacturerId>:<modelId>` pairs so the merchandisers endpoint
	// filters on the exact combination; no queryMapping, mirroring the other
	// filters on this dedicated endpoint.
	merchandiserModel: {
		type: 'multi-select',
		label: 'Make & Model',
		placeholder: 'Search make or model...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		menuMinWidth: 300,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			secondary: 'subtitle' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/merchandisers/model/options',
		queryParamName: 'makeModel',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// Fill level (PD-12625) — the headline refill-planning filter on the
	// merchandiser map. Derived from the inventory rollup (stockLevel), so it
	// has no queryMapping; the map endpoint filters on the computed value.
	merchandiserFillLevel: {
		type: 'multi-select',
		label: 'Fill Level',
		placeholder: 'Select fill level...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		options: [
			{ id: 'Empty', label: 'Empty' },
			{ id: 'Low', label: 'Low' },
			{ id: 'Medium', label: 'Medium' },
			{ id: 'Full', label: 'Full' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'fillLevel',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// Health (PD-15733) — the managed sensor-driven state, derived from the
	// unit's open sensor alerts + device attachment (an open offline alert
	// wins, any other open alert needs attention, a sensored unit with a
	// clean slate is healthy, no sensor at all is its own state). Computed
	// server-side, so no queryMapping; the merchandisers endpoint reads the
	// `health` param and filters on the derived value.
	merchandiserHealth: {
		type: 'multi-select',
		label: 'Health',
		placeholder: 'Select health...',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		options: [
			{ id: 'healthy', label: 'Healthy' },
			{ id: 'needs_attention', label: 'Needs Attention' },
			{ id: 'offline', label: 'Offline' },
			{ id: 'no_sensors', label: 'No Sensors' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'health',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// High temperature (PD-13938) — derived quick-filter over the temperature
	// bubbled up from the attached sensor device
	// (merchandiser.currentTemperature). Single-option: when set, the
	// merchandisers backend reads the `highTemp` param and applies
	// currentTemperature >= MERCHANDISER_HIGH_TEMP_THRESHOLD (the
	// team-tunable constant in @supply-mover-2-0/shared). No queryMapping —
	// mirrors deviceFilter below.
	merchandiserHighTemp: {
		type: 'single-select',
		label: 'Temperature',
		placeholder: 'Any temperature',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'high', label: 'High Temp' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'highTemp',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// --- Merchandiser template list (PD-15559) ---
	merchandiserTemplateSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Template name or description...',
			objectTable: 'merchandiser_template',
			listKeys: ['merchandiser_template'],
		}),
		queryParamName: 'search',
	},

	// A template is either in use or retired. The list defaults to Active
	// (see MERCHANDISER_TEMPLATE_LIST_CONFIG.filters.defaultFilters) because a
	// retired setup is history rather than something an operator is choosing
	// from; clearing the chip shows both. isActive is a real column, but the
	// endpoint reads the chip as `activeFilter` and applies it itself, so
	// there is no queryMapping (mirrors deviceFilter above).
	merchandiserTemplateActive: {
		type: 'single-select',
		label: 'Status',
		placeholder: 'All templates',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'active', label: 'Active' },
			{ id: 'inactive', label: 'Retired' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'activeFilter',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser_template'],
	},

	// --- Sensor device list ---
	sensorDeviceSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Device ID, name, merchandiser...',
			objectTable: 'sensor_device',
			listKeys: ['sensor_device'],
		}),
		queryParamName: 'search',
	},

	// --- Device reading log (PD-15734, merchandiser/device detail) ---
	// Timeframe over the reading timestamp. The value is the standard
	// date-range shape ({startDate, endDate}); the dedicated readings
	// endpoint applies it as from/to, so there is no queryMapping.
	sensorReadingTimeframe: {
		type: 'date-range',
		label: 'Timeframe',
		placeholder: 'Select date range...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'timeframe',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		listKeys: ['sensorReading-device-embedded'],
	},

	// Module selector for the device reading log. Devices carry up to 8
	// addressable modules; readings for module numbers a device doesn't have
	// simply don't exist, so over-offering is harmless. Resolved by the
	// dedicated endpoint (moduleId param) — no queryMapping.
	sensorReadingModule: {
		type: 'single-select',
		label: 'Module',
		placeholder: 'All modules',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: '1', label: 'Module 1' },
			{ id: '2', label: 'Module 2' },
			{ id: '3', label: 'Module 3' },
			{ id: '4', label: 'Module 4' },
			{ id: '5', label: 'Module 5' },
			{ id: '6', label: 'Module 6' },
			{ id: '7', label: 'Module 7' },
			{ id: '8', label: 'Module 8' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'moduleId',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['sensorReading-device-embedded'],
	},

	// Derived quick-filter — NOT a single column. "Needs Attention" is a
	// compound health condition (offline/error/stale/low-battery), "Has
	// Orphans" is a post-count comparison (assigned < total), and
	// "Unattached" is merchandiserId IS NULL. So there is no queryMapping:
	// the sensor-devices backend reads the `deviceFilter` param and applies
	// the condition itself (mirrors vehicleTripStatus).
	deviceFilter: {
		type: 'single-select',
		label: 'Show',
		placeholder: 'All devices',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'needs_attention', label: 'Needs Attention' },
			{ id: 'has_orphans', label: 'Has Orphans' },
			{ id: 'unattached', label: 'Unattached' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'deviceFilter',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['sensor_device'],
	},
};
